import {
  ActionIcon,
  Box,
  Group,
  Input,
  TextInput,
  Text,
  Tooltip,
  Button,
  Menu,
  FileInput,
  Stack,
  Popover,
} from "@mantine/core";
import React, { useState, useRef, useEffect } from "react";
import {
  IconMoodHappy,
  IconPhoto,
  IconCirclePlus,
  IconFolder,
} from "@tabler/icons";
import EmojiPicker from "emoji-picker-react";
import { useClickOutside, getHotkeyHandler } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
// import { socket } from "../services/socket";
import dayjs from "dayjs";
import { date } from "zod";

const MAX_IMAGE_FILE_SIZE = 5 * 1024 * 1024; //5M
const MAX_VIDEO_FILE_SIZE = 10 * 1024 * 1024; //5M

const checkFileSize = (fileSize, limit) => {
  if (fileSize > limit) {
    showNotification({
      title: `文件大小超过${limit / (1024 * 1024)}M`,
      message: "请压缩后再次重试",
      color: "red",
    });
    return false;
  }
  return true;
};

function ChatInput({ user, socket, setMessages }) {
  const [isEmojiPickerActive, setIsEmojiPickerActive] = useState(false);
  const [isMultiPickerActive, setIsMultiPickerActive] = useState(false);
  const textInputRef = useRef(null);
  const albumInputRef = useRef(null);
  const emojiRef = useClickOutside(() => setIsEmojiPickerActive(false));
  const multiPickerRef = useClickOutside(() => setIsMultiPickerActive(false));

  const handleEmojiSelection = (emojiData) => {
    if (textInputRef && textInputRef.current) {
      textInputRef.current.value += emojiData.emoji;
    }
  };

  const handleUpload = (e) => {
    const { files } = e.target;

    if (files[0].type.includes("image")) {
      if (files[0].size > MAX_IMAGE_FILE_SIZE) {
        showNotification({
          title: `文件大小超过${MAX_IMAGE_FILE_SIZE / (1024 * 1024)}M`,
          message: "请压缩后再次重试",
          color: "red",
        });
        return;
      }

      // Show image locally without getting it from websocket
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          username: user.username,
          blob: files[0],
          type: "image",
          time: new Date(),
          id: user.id,
        },
      ]);
      socket?.emit("chatMessage", {
        username: user.username,
        blob: files[0],
        type: "image",
      });
      return;
    }

    if (files[0].type.includes("video")) {
      if (files[0].size > MAX_VIDEO_FILE_SIZE) {
        showNotification({
          title: `文件大小超过${MAX_VIDEO_FILE_SIZE / (1024 * 1024)}M`,
          message: "请压缩后再次重试",
          color: "red",
        });
        return;
      }
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          username: user.username,
          blob: files[0],
          type: "video",
          time: new Date(),
          id: user.id,
        },
      ]);

      socket?.emit("chatMessage", {
        username: user.username,
        blob: files[0],
        type: "video",
      });
      return;
    }
  };

  const handleSubmit = () => {
    if (textInputRef.current.value) {
      socket?.emit("chatMessage", {
        username: user.username, //"nrh",
        text: textInputRef.current.value,
        type: "text",
      });
      textInputRef.current.value = "";
    }
  };

  return (
    <Box
      py='5px'
      px='2px'
      sx={(theme) => ({
        backgroundColor: theme.colors.gray[0],
        position: "relative",
      })}
    >
      <Group position='apart'>
        <TextInput
          ref={textInputRef}
          placeholder='输入消息...'
          sx={{ flex: "1" }}
          onKeyUp={getHotkeyHandler([["Enter", handleSubmit]])}
          rightSection={
            <Tooltip label='选择一个表情' position='top-end' withArrow>
              <ActionIcon
                onClick={() => setIsEmojiPickerActive((prev) => !prev)}
              >
                <IconMoodHappy size={24} />
              </ActionIcon>
            </Tooltip>
          }
        />
        <Group>
          <Box>
            <Tooltip
              label='选择图片、视频或者文件'
              position='top-end'
              withArrow
            >
              <ActionIcon
                onClick={() => setIsMultiPickerActive((prev) => !prev)}
              >
                <IconCirclePlus size={24} />
              </ActionIcon>
            </Tooltip>
          </Box>

          <Button
            variant='gradient'
            gradient={{ from: "teal", to: "blue", deg: 60 }}
            onClick={handleSubmit}
          >
            发送
          </Button>
        </Group>
      </Group>
      <Box sx={{ width: "100%" }}>
        {isEmojiPickerActive && (
          <Box
            ref={emojiRef}
            sx={{
              position: "absolute",
              right: 0,
              bottom: "2.5rem",
            }}
          >
            <EmojiPicker
              // lazyLoadEmojis
              searchDisabled
              skinTonesDisabled
              onEmojiClick={handleEmojiSelection}
            />
          </Box>
        )}
        {isMultiPickerActive && (
          <Box ref={multiPickerRef} m='1rem'>
            <Group position='center' spacing='xl'>
              <label style={{ textAlign: "center" }}>
                <IconPhoto size={34} />
                <Text size='sm'>相册</Text>
                <input
                  ref={albumInputRef}
                  type='file'
                  accept='image/*, video/*'
                  style={{ display: "none" }}
                  onChange={handleUpload}
                />
              </label>
              <Popover
                width={200}
                position='bottom'
                withArrow
                shadow='md'
                radius='xl'
              >
                <Popover.Target>
                  <label style={{ textAlign: "center" }}>
                    <IconFolder size={34} style={{ opacity: 0.5 }} />
                    <Text size='sm'>文件</Text>
                    <input
                      // ref={albumInputRef}
                      disabled
                      type='file'
                      accept='image/*, video/*'
                      style={{ display: "none" }}
                      onChange={handleUpload}
                    />
                  </label>
                </Popover.Target>
                <Popover.Dropdown
                  sx={(theme) => ({
                    backgroundColor: theme.colors.orange[3],
                  })}
                >
                  <Text size='sm'>该功能正在开发中，请稍后查看</Text>
                </Popover.Dropdown>
              </Popover>
            </Group>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default ChatInput;
